import {Container, Modal, ModalBody, ModalHeader} from "reactstrap";
import {observer} from "mobx-react-lite";
import React, {useCallback, useEffect, useMemo, useState} from "react";
import {useTranslation} from "react-i18next";
import "./eventTemplatesModal.sass";
import Skeleton from "react-loading-skeleton";
import EventTemplateList from "../../molecules/eventTemplateList/eventTemplateList";
import EventTemplateCategory from "../../../stores/domainStore/pages/models/eventTemplateCategory";
import {useStores} from "../../../stores/useStores";
import NoResultsIcon from "../../atoms/noResultsIcon/noResultsIcon";
import EventTemplateCard from "../../atoms/eventTemplateCard/eventTemplateCard";
import Spinner from "../../atoms/common/spinner/spinner";
import SearchBox, {SearchBoxType} from "../../atoms/input/searchBox";
import MenuItem from "../../atoms/menu/menuItem";
import {IconSource} from "../../../shared/enums/iconSource";
import NavigationButton from "../../atoms/button/navigationButton";
import {runInAction} from "mobx";
import {TextObservable} from "../../../stores/observables/textObservable";
import debounce from "lodash/debounce";
import {EventTemplateStatus} from "../../../shared/enums/eventTemplateStatus";
import {EventCatalogueSortField} from "../../../shared/enums/eventCatalogueSortField";
import {EventTemplateCategoryType} from "../../../shared/enums/eventTemplateCategoryType";
import HeaderColor from "../../atoms/headerColor/headerColor";
import {getEventCalendarTypeDisplay} from "../../../shared/eventCalendarTypeHelper";

export interface IEventTemplatesModalProps {
  isLoading: boolean;
  isOpen: boolean;
  hideFeature: boolean;
  toggle: () => void;
  eventTemplateCategories: EventTemplateCategory[];
  selectedTemplateCategories: EventTemplateCategory[];
  onTemplateCategoryChanged: (eventTemplate: string) => void;
  onUseTemplate: (id: string) => void;
  hasData: boolean;
}

const EventTemplatesModal: React.FC<IEventTemplatesModalProps> = ({
  isLoading,
  isOpen,
  hideFeature = true,
  toggle,
  eventTemplateCategories,
  selectedTemplateCategories,
  onTemplateCategoryChanged,
  onUseTemplate,
  hasData
}) => {
  const {
    uiStore: {eventCatalogueUiStore, eventsUiStore}
  } = useStores();

  const categoryTypeIcon: {[key: string]: IconSource} = {
    VillageFavourites: IconSource.favourite,
    RecentlyAdded: IconSource.recent
  };
  const [activeEventCategory, setActiveEventCategory] = useState<string>("");
  const {t} = useTranslation(["translation", "event"]);
  let eventCategoryContainer: any;

  useEffect(() => {
    if (!isLoading) {
      setActiveEventCategory("");
    }
  }, [isLoading, isOpen]);

  useEffect(() => {
    if (eventCatalogueUiStore.eventCatalogueSearchText !== "") {
      // Ensure we are on "all categories" when searching
      setActiveEventCategory("");
    }
  }, [eventCatalogueUiStore.eventCatalogueSearchText]);

  useEffect(() => {
    // Set the scroll bar to the top if we change category
    if (eventCategoryContainer) {
      eventCategoryContainer.scrollTop = 0;
    }
  }, [eventCategoryContainer, selectedTemplateCategories]);

  useEffect(() => {
    if (!isOpen) {
      // reset all values to default on modal close
      runInAction(() => (eventCatalogueUiStore.eventCatalogueSearchText = ""));
      eventCatalogueUiStore.isEventTemplateBodyLoading.setValue(false);
      setIsGroupedResult(true);
    }
  }, [eventCatalogueUiStore, isOpen]);

  const setActiveCategory = (id: string): void => {
    runInAction(() => {
      // We must have empty search box when changing category
      eventCatalogueUiStore.eventCatalogueSearchText = "";
    });
    setIsGroupedResult(true);
    setActiveEventCategory(id);
    onTemplateCategoryChanged(id);
  };

  const [isGroupedResult, setIsGroupedResult] = useState(true);

  const searchTemplates = (): void => {
    const searchTerm = eventCatalogueUiStore.eventCatalogueSearchText.trim();
    eventCatalogueUiStore
      .loadEventCatalogue(
        EventTemplateStatus.Active,
        searchTerm === ""
          ? eventCatalogueUiStore.minSearchResultLimit
          : eventCatalogueUiStore.maxSearchResultLimit,
        EventCatalogueSortField.title,
        searchTerm,
        true,
        eventsUiStore.calendarTypeBySelectedCalendarName
      )
      .finally(() => {
        setIsGroupedResult(searchTerm === "");
      });
  };

  // This is added to ensure the debounce function works as expected.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const search = useCallback(
    debounce(() => {
      searchTemplates();
    }, 500),
    []
  );
  const searchTerm: TextObservable = new TextObservable(
    eventCatalogueUiStore.eventCatalogueSearchText,
    undefined,
    undefined,
    () => {
      runInAction(() => {
        eventCatalogueUiStore.eventCatalogueSearchText = searchTerm.text;
      });
      search();
    }
  );

  selectedTemplateCategories = selectedTemplateCategories ?? [];

  const eventTemplateCategoryList = renderEventTemplateCategoryList();

  function renderEventTemplateCategoryList(): JSX.Element[] {
    if (isGroupedResult) {
      // We have not searched, group the results
      return selectedTemplateCategories
        .filter((category) => category.eventTemplates.length > 0)
        .map((eventTemplateCategory) => (
          <EventTemplateList
            key={eventTemplateCategory.id}
            eventTemplateCategory={eventTemplateCategory}
            showSeeAll={!activeEventCategory}
            onUseTemplate={onUseTemplate}
            onSelectTemplateCategory={setActiveCategory}
          />
        ));
    } else {
      const templateCards = selectedTemplateCategories
        .flatMap((category) => category.eventTemplates)
        .sort((templateA, templateB) =>
          templateA.title.localeCompare(templateB.title)
        )
        .map((template) => (
          <EventTemplateCard
            key={template.id}
            imageSrc={template.image}
            description={template.description}
            imageId={template.imageId}
            title={template.title}
            onClick={onUseTemplate}
            templateId={template.id}
            isNew={template.isNew}
          />
        ));

      // Only add "wrapper" if there is at least 1 card
      if (templateCards.length === 0) {
        return [];
      }

      return [
        <>
          {searchTerm.text && (
            <div className="results">
              {`${templateCards.length} `}
              {templateCards.length > 1
                ? t("eventTemplate:labels.results")
                : t("eventTemplate:labels.result")}
            </div>
          )}
          <div key="searchResults" className="search-results">
            {templateCards}
          </div>
        </>
      ];
    }
  }

  function renderResultsBody(): JSX.Element {
    if (eventCatalogueUiStore.isEventTemplateBodyLoading.value) {
      return <Spinner />;
    }

    if (hasData && eventTemplateCategoryList?.length === 0) {
      return (
        <NoResultsIcon
          titleText={
            eventCatalogueUiStore.eventCatalogueSearchText
              ? t("eventTemplate:messages.noSearchResults")
              : t("eventTemplate:messages.noResults")
          }
          bodyText={eventCatalogueUiStore.eventCatalogueSearchText.trim()}
        />
      );
    }

    return <>{eventTemplateCategoryList}</>;
  }

  const getTemplateCategoryMenuItem = (
    type: EventTemplateCategoryType,
    showIcon: boolean
  ) => {
    return eventTemplateCategories?.map(
      (templateCategory) =>
        templateCategory.categoryType === type && (
          <MenuItem
            icon={showIcon ? categoryTypeIcon[templateCategory.id] : undefined}
            key={templateCategory.id}
            title={templateCategory.name}
            scoped={true}
            selected={activeEventCategory === templateCategory.id}
            onClick={() => setActiveCategory(templateCategory.id)}
          />
        )
    );
  };

  const calendarColor = useMemo(
    () => eventsUiStore.calendarColor,
    [eventsUiStore.selectedCalendarName]
  );

  return (
    <Modal
      isOpen={isOpen}
      modalClassName="theme-modal event-templates-modal"
      contentClassName="border"
      centered
      size="xl"
      data-testid="eventTemplatesModalTest"
    >
      {calendarColor && <HeaderColor color={calendarColor}></HeaderColor>}
      <ModalHeader
        toggle={toggle}
        style={{wordBreak: "break-word"}}
        close={<NavigationButton icon={IconSource.exit} onClick={toggle} />}
      >
        {t("event:titles:createNewEvent")}
      </ModalHeader>
      <ModalBody className="content-padding">
        <Container>
          {isLoading ? (
            <>
              <Skeleton className="body" />
            </>
          ) : (
            <>
              <div className="row">
                <div className="col-md-2 event-category-list">
                  <div className="menu-items">
                    <MenuItem
                      title={t(
                        getEventCalendarTypeDisplay(
                          eventsUiStore.calendarTypeBySelectedCalendarName
                        ).templatesTitleKey
                      )}
                      icon={IconSource.home}
                      scoped={true}
                      selected={!activeEventCategory}
                      onClick={() => setActiveCategory("")}
                    />
                    {getTemplateCategoryMenuItem(
                      EventTemplateCategoryType.villageFavourite,
                      true
                    )}
                    {getTemplateCategoryMenuItem(
                      EventTemplateCategoryType.recentlyAdded,
                      true
                    )}
                  </div>
                  <div className="category-separator">
                    <div className="navbar-label">By Category</div>
                    <hr />
                  </div>
                  <div className="all-categories-list">
                    {getTemplateCategoryMenuItem(
                      EventTemplateCategoryType.normal,
                      false
                    )}
                  </div>
                </div>
                <div className="col list-view">
                  <div
                    className="body"
                    ref={(el) => (eventCategoryContainer = el)}
                  >
                    <div className="search-box">
                      <SearchBox
                        type={SearchBoxType.Live}
                        value={searchTerm}
                        placeholder="Search events. e.g fine dining"
                        onClear={() => setActiveCategory("")}
                      />
                    </div>
                    <div>{renderResultsBody()}</div>
                  </div>
                </div>
              </div>
            </>
          )}
        </Container>
      </ModalBody>
    </Modal>
  );
};

export default observer(EventTemplatesModal);
